import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import BookLocationHeader from "../../common/util/BookLocationHeader";
import TenantLogo from "../../../layout/components/TenantLogo";
import BookLocationNavigateNextAction from "../../common/util/BookLocationNavigateNextAction";
import BookLocationNavigateBackAction from "../../common/util/BookLocationNavigateBackAction";
import GeneralDialog from "../../../common/GeneralDialog";
// Removed MUI Box in favor of Tailwind-based wrappers
import { baseStore, RootState } from "../../../../redux/store";
import { usePrevious } from "../../../../util/usePrevious";
import {
    bookingCreationCompleted,
    bookingFlowChanged,
    bookingStepChanged,
    dateSelectedClear,
    durationSelectedClear,
    navigatedToNextStep,
    setAvailabilities,
    setHasSelectedLocation,
    setSelectedLocation,
    setUseCase,
    setAppliedCouponCode,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
// Removed MUI styled in favor of Tailwind-based wrappers
import KeyHandoverLocationSelectionStep from "../../keyHandover/steps/KeyHandoverLocationSelectionStep";
import KeyHandoverScheduleStep from "../../keyHandover/steps/KeyHandoverScheduleStep";
import { availabilitiesApiSlice } from "../../../../api/availabilities/availabilitiesApiSlice";
import KeyHandoverBookingSummaryStep from "../../keyHandover/steps/KeyHandoverBookingSummaryStep";
import { useCreateNewReservation } from "../../../../redux/flows/keyHandoverCreateReservationFlow";
import { useRemoveReservation } from "../../../../redux/flows/removeReservationFlow";
import { useFetchGroups } from "../../../../redux/flows/fetchGroupsFlow";
import KeyHandoverPaymentStep from "../../keyHandover/steps/KeyHandoverPaymentStep";
import routes from "../../../../routes/routes";
import { getEnumNameByNumber, getEnumNumberByName } from "../../../../util/enum";
import {
    BookingCreationERStep,
    BookingCreationP2PStep,
    setKeyRecipientPhase,
    setNextEnabled,
    setStep,
    setKeyPaymentModel,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { useApplyCoupon } from "../../../../redux/flows/applyCouponFlow";
import Header from "../../../layout/components/Header";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { Button } from "../../../../components/ui/button";
import { getSvcLocations } from "../../../../util/utilFunctions";
import { ServiceTypeSelectorOption } from "../../../../types/order";
import useServiceStorage from "../../../../hooks/useServiceStorage";
import KeyHandoverGuestDetailsStep from "../../keyHandover/steps/KeyHandoverGuestDetailsStep";
import KeyHandoverServiceSelectionStep from "../../keyHandover/steps/KeyHandoverServiceSelectionStep";
import KeyHandoverReturnScheduleStep from "../../keyHandover/steps/KeyHandoverReturnScheduleStep";
import { KeyHandoverServiceProvider } from "../context/KeyHandoverServiceContext";
import { KeyHandoverScheduleProvider } from "../context/KeyHandoverScheduleContext";
import { KeyHandoverReturnScheduleProvider } from "../context/KeyHandoverReturnScheduleContext";
import KeyHandoverHostDetailsStep from "../steps/KeyHandoverHostDetailsStep";
import { KeyHandoverSubscriptionProvider } from "../context/KeyHandoverSubscriptionContext";
import KeyHandoverPaymentModelStep from "../steps/KeyHandoverPaymentModelStep";
import { useBookingWindow } from "../../common/hooks/useBookingWindow";

export enum BookingCreationStep {
    locationSelection, // 0
    paymentModelSelection, // 1
    assetGroupSelection, // 2
    orderData, // 3
    landlordData, // 4
    recipientData, // 5
    confirmation, // 6
    personalData, // 7
    paymentTypeSelection, // 8
    payment, // 9
}

// Tailwind wrappers replacing MUI Box/styled
const RootDiv = ({ children }: { children: ReactNode }) => (
    <div
        className="h-full box-border bg-background flex flex-col px-3 py-2"
        style={{ paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0))" }}
    >
        {children}
    </div>
);

const StepWrapperDiv = ({ children, id }: { children: ReactNode; id?: string }) => (
    <div id={id} className="flex-1 w-full max-w-[1000px] mx-auto overflow-y-auto pt-6">
        {children}
    </div>
);

const ActionsWrapperDiv = ({ children }: { children: ReactNode }) => (
    <div className="w-[calc(100%-42px)] max-w-[1000px] mx-auto pb-10">{children}</div>
);

const KeyHandoverBookingPage = () => {
    const [searchParams] = useSearchParams();
    const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
    const dispatch = useDispatch();
    // The configured booking horizon for this use case — replaces the hardcoded
    // `add(1, "months").endOf("month")` this file computed inline. One month is
    // the DEFAULT here, so an unconfigured tenant requests exactly the same range
    // as before. `earliest` carries the lead buffer, which defaults to 0 for this
    // use case (the customer may book now), and is the same rule the app API
    // enforces (ADR-0020, `backend/src/core/lib/bookingWindow.ts`).
    const bookingWindow = useBookingWindow("keys");

    const { t } = useTranslation();
    const navigate = useNavigate();
    const step = useSelector((state: RootState) => state.bookingCreation.step);
    const useCase = useSelector((state: RootState) => state.bookingCreation.useCase);
    const locations = useSelector((state: RootState) => state.booking.locations);
    const { updateService } = useServiceStorage("selectedService", ServiceTypeSelectorOption.KEYS);
    const previousStep = usePrevious(step);
    const reservations = useSelector((state: RootState) => state.bookingCreation.reservations);
    const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);
    const selectedLocation = useSelector(
        (state: RootState) => state.bookingCreation.selectedLocation
    );
    const assetGroupId = useSelector((state: RootState) => state.bookingCreation.assetGroupId);
    const appliedCouponCode = useSelector(
        (state: RootState) => state.bookingCreation.appliedCouponCode
    );
    const selectedServiceType = useSelector(
        (state: RootState) => state.bookingCreation.keyHandoverSelectedService
    );
    const bookingFlow = useSelector((state: RootState) => state.bookingCreation.bookingFlow);
    const nextEnabled = useSelector((state: RootState) => state.bookingCreation.nextEnabled);
    const [bounceNext, setBounceNext] = useState(false);
    const [prevNextEnabled, setPrevNextEnabled] = useState<boolean>(nextEnabled);
    const keyRecipientPhase = useSelector(
        (state: RootState) => state.bookingCreation.keyRecipientPhase
    );
    // const bookingFlow = useSelector(
    //   (state: RootState) => state.bookingCreation.bookingFlow
    // );

    const location = useLocation();
    // Get customerAgreementId from URL if present
    const params = new URLSearchParams(location.search);
    const customerAgreementId = params.get("customerAgreementId") || undefined;
    const createNewReservation = useCreateNewReservation("keys", customerAgreementId);

    const applyCoupon = useApplyCoupon();
    const removeReservation = useRemoveReservation();
    const fetchGroups = useFetchGroups("keys");

    useEffect(() => {
        // Capture voucher code passed via URL (e.g., from homepage banner) and store for later application
        const voucherCode = searchParams.get("voucher");
        if (voucherCode && voucherCode.trim() && !appliedCouponCode) {
            dispatch(setAppliedCouponCode(voucherCode.trim()));
        }
    }, [dispatch, searchParams, appliedCouponCode]);

    useEffect(() => {
        if (step === BookingCreationStep.locationSelection) {
            dispatch(
                bookingStepChanged({
                    step: BookingCreationStep.locationSelection,
                    nextEnabled: !!selectedLocation,
                })
            );

            // Set the usecase
            dispatch(setUseCase({ useCase: "keys" }));

            // Set the bookingFlow; for Pickup-And-Return, move return date step before landlord data
            const isCombined = selectedServiceType === "Key-Pickup-And-Return";
            const flow = [
                "locationSelection",
                "paymentModelSelection",
                "assetGroupSelection",
                "orderData",
                isCombined ? "recipientData" : undefined,
                "landlordData",
                !isCombined ? "recipientData" : undefined,
                "confirmation",
                "payment",
            ].filter(Boolean) as string[];
            dispatch(
                bookingFlowChanged({
                    bookingFlow: flow,
                })
            );
        }
    }, [dispatch, selectedLocation, step, selectedServiceType]);

    // Sync bookingFlow if service type changes after initial setup
    useEffect(() => {
        const isCombined = selectedServiceType === "Key-Pickup-And-Return";
        const desiredFlow = [
            "locationSelection",
            "paymentModelSelection",
            "assetGroupSelection",
            "orderData",
            isCombined ? "recipientData" : undefined,
            "landlordData",
            !isCombined ? "recipientData" : undefined,
            "confirmation",
            "payment",
        ].filter(Boolean) as string[];
        const current = (bookingFlow || []) as string[];
        if (current.join("|") !== desiredFlow.join("|")) {
            dispatch(bookingFlowChanged({ bookingFlow: desiredFlow }));
        }
    }, [dispatch, selectedServiceType, bookingFlow]);

    const fetchAssetGroupAvailabilities = async (assetGroupId: string, from: Date, to: Date) => {
        const data = await baseStore.dispatch(
            availabilitiesApiSlice.endpoints.fetchAssetGroupAvailabilities.initiate({
                assetGroupId: assetGroupId,
                from: from,
                to: to,
            })
        );
        return data;
    };

    // Note: Do not reset the flow on confirmation when there are no reservations.
    // For the keys flow, we create a reservation upon entering confirmation (see manageReduxData effect).
    // Resetting here would incorrectly send users back to location selection.

    useEffect(() => {
        const manageReduxData = async () => {
            if (
                step === BookingCreationStep.paymentModelSelection &&
                previousStep !== BookingCreationStep.paymentModelSelection
            ) {
                // Fetch services early to show pricing in subscription model step
                await fetchGroups();
            } else if (
                step === BookingCreationStep.assetGroupSelection &&
                previousStep !== BookingCreationStep.assetGroupSelection
            ) {
                // Clean data
                dispatch(dateSelectedClear());
                dispatch(durationSelectedClear());
                // Services already loaded in subscription step, no need to fetch again
            } else if (
                step === BookingCreationStep.orderData &&
                assetGroupId &&
                previousStep &&
                !(previousStep > BookingCreationStep.orderData)
            ) {
                // TODO change to user input
                const fromDate = bookingWindow.earliest.toDate();
                const toDate = bookingWindow.latest.toDate();
                const { data: availabilities } = await fetchAssetGroupAvailabilities(
                    assetGroupId,
                    fromDate,
                    toDate
                );

                if (availabilities) {
                    dispatch(setAvailabilities(availabilities.availabilities));
                }
            }
            // // previous step is orderData, current step is personal data
            if (
                previousStep === BookingCreationStep.recipientData &&
                step === BookingCreationStep.confirmation
            ) {
                if (reservations.length === 0) {
                    const processedReservation = await createNewReservation();
                    updateService(ServiceTypeSelectorOption.KEYS);

                    if (processedReservation && appliedCouponCode) {
                        const reservationIds = reservations.map(
                            (reservation) => reservation.reservationId
                        );
                        reservationIds.push(processedReservation.reservationId!);
                        await applyCoupon(reservationIds, appliedCouponCode);
                    }
                }
            }

            if (
                step === BookingCreationStep.orderData &&
                assetGroupId &&
                previousStep &&
                previousStep > BookingCreationStep.orderData
            ) {
                if (reservations.length > 0) {
                    // user navigated back, so delete reservation
                    await removeReservation();
                }
                const fromDate = bookingWindow.earliest.toDate();
                const toDate = bookingWindow.latest.toDate();
                const { data: availabilities } = await fetchAssetGroupAvailabilities(
                    assetGroupId,
                    fromDate,
                    toDate
                );
                if (availabilities) {
                    dispatch(setAvailabilities(availabilities.availabilities));
                }
            }

        };
        manageReduxData();
        // We intentionally exclude function refs (applyCoupon, createNewReservation, removeReservation, updateService, fetchGroups)
        // because they are stable in behavior but can be recreated per render by their hooks, causing infinite loops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, step, assetGroupId, previousStep, reservations, appliedCouponCode]);

    const stepName = useMemo(() => {
        const enumRef =
            useCase === "rental" || useCase === "sup"
                ? BookingCreationERStep
                : BookingCreationP2PStep;
        const name = getEnumNameByNumber(enumRef as any, step);
        return name;
    }, [step, useCase, bookingFlow]);

    // Bounce the Next button on the schedule step when it transitions from disabled -> enabled
    useEffect(() => {
        const isSchedule = stepName === "orderData";
        const isCombined = selectedServiceType === "Key-Pickup-And-Return";
        if (isSchedule && isCombined && nextEnabled && !prevNextEnabled) {
            setBounceNext(true);
            const t = setTimeout(() => setBounceNext(false), 450);
            return () => clearTimeout(t);
        }
        setPrevNextEnabled(nextEnabled);
    }, [nextEnabled, prevNextEnabled, stepName, selectedServiceType]);

    const headerText = useMemo(() => {
        switch (stepName) {
            case "locationSelection":
                return t("book_location.select_location");
            case "paymentModelSelection":
                return t("book_location.keys.subscription_model.header_text", {
                    defaultValue: "Payment Model",
                });
            case "assetGroupSelection":
                return t("book_location.keys.service");
            case "orderData":
                return t("book_location.keys.order_data.header_text", {
                    defaultValue: t("book_location.order_data.header_text"),
                });
            case "landlordData":
                return t("book_location.keys.landlord_data.header_text", {
                    defaultValue: "Host details",
                });
            case "recipientData":
                if (selectedServiceType === "Key-Pickup-And-Return") {
                    return keyRecipientPhase === "guest"
                        ? t("book_location.recipient_data.header_text2")
                        : t("book_location.keys.return_data.header_text", {
                              defaultValue: t("book_location.recipient_data.header_text2"),
                          });
                }
                return t("book_location.recipient_data.header_text2");
            case "confirmation":
                return t("book_location.confirmation.header_text");
            // "personalData" isn't part of the keys flow anymore
            case "personalData":
                return t("book_location.personal_data.header_text");
            default:
                return "";
        }
    }, [stepName, t, selectedServiceType, keyRecipientPhase]);

    const closePage = () => {
        // history.goBack();
        navigate(routes.home);
        dispatch(bookingCreationCompleted());
    };

    // const handleAddAnotherEquipment = () => {
    //   dispatch(addAnotherGroup({}));
    // };

    // Auto-skip disabled to always show subscription model selection step
    // useEffect(() => {
    //   if (locations && step === BookingCreationStep.locationSelection) {
    //     const svcLocations = getSvcLocations(locations, "keys");
    //     if (svcLocations.length === 1) {
    //       dispatch(navigatedToNextStep());
    //       dispatch(setSelectedLocation(svcLocations[0]));
    //       dispatch(setHasSelectedLocation(true));
    //     }
    //   }
    // }, [dispatch, locations, step]);

    return (
        <RootDiv>
            <Header>
                <div className="h-12 absolute left-2 flex items-center">
                    <BookLocationNavigateBackAction
                        onClickOverride={
                            selectedServiceType === "Key-Pickup-And-Return" &&
                            (stepName === "recipientData" ||
                                stepName === "landlordData" ||
                                stepName === "confirmation")
                                ? () => {
                                      const enumRef =
                                          useCase === "rental" || useCase === "sup"
                                              ? BookingCreationERStep
                                              : BookingCreationP2PStep;
                                      if (stepName === "recipientData") {
                                          if (keyRecipientPhase === "guest") {
                                              // From guest data → back to landlord
                                              const landlordStepNum = getEnumNumberByName(
                                                  enumRef as any,
                                                  "landlordData"
                                              );
                                              dispatch(setStep(landlordStepNum));
                                              dispatch(setNextEnabled(true));
                                          } else {
                                              // From return scheduling → back to handover
                                              const handoverStepNum = getEnumNumberByName(
                                                  enumRef as any,
                                                  "orderData"
                                              );
                                              dispatch(setStep(handoverStepNum));
                                              const hasBoth = Boolean(
                                                  orderData.checkInDate && orderData.checkOutDate
                                              );
                                              dispatch(setNextEnabled(hasBoth));
                                          }
                                      } else if (stepName === "landlordData") {
                                          // From landlord → back to return scheduling (Schlüsselrückgabe)
                                          const recipientStepNum = getEnumNumberByName(
                                              enumRef as any,
                                              "recipientData"
                                          );
                                          dispatch(setKeyRecipientPhase("return"));
                                          dispatch(setStep(recipientStepNum));
                                          const hasReturn = Boolean(
                                              orderData.keyReturnFromDate &&
                                                  orderData.keyReturnToDate
                                          );
                                          dispatch(setNextEnabled(hasReturn));
                                      } else if (stepName === "confirmation") {
                                          // From summary → back to guest data (combined flow)
                                          const recipientStepNum = getEnumNumberByName(
                                              enumRef as any,
                                              "recipientData"
                                          );
                                          dispatch(setKeyRecipientPhase("guest"));
                                          dispatch(setStep(recipientStepNum));
                                      }
                                  }
                                : undefined
                        }
                    />
                </div>
                <TenantLogo />
                <Button
                    id="closeButton"
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.close", { defaultValue: "Close" })}
                    onClick={() => setPageDialogOpen(true)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full text-foreground/90"
                >
                    <CloseIcon />
                </Button>
            </Header>
            <BookLocationHeader text={headerText} />
            <StepWrapperDiv id={"key-handover-process-step-wrapper"}>
                {stepName === "locationSelection" && <KeyHandoverLocationSelectionStep />}
                {stepName === "paymentModelSelection" && (
                    <KeyHandoverSubscriptionProvider>
                        <KeyHandoverPaymentModelStep />
                    </KeyHandoverSubscriptionProvider>
                )}
                {stepName === "assetGroupSelection" && (
                    <KeyHandoverServiceProvider typesFilter="keys">
                        <KeyHandoverServiceSelectionStep />
                    </KeyHandoverServiceProvider>
                )}
                {stepName === "orderData" && (
                    <KeyHandoverScheduleProvider>
                        <KeyHandoverScheduleStep />
                    </KeyHandoverScheduleProvider>
                )}
                {stepName === "landlordData" && <KeyHandoverHostDetailsStep />}
                {stepName === "recipientData" &&
                    (selectedServiceType === "Key-Pickup-And-Return" ? (
                        keyRecipientPhase === "guest" ? (
                            <KeyHandoverGuestDetailsStep />
                        ) : (
                            <KeyHandoverReturnScheduleProvider>
                                <KeyHandoverReturnScheduleStep />
                            </KeyHandoverReturnScheduleProvider>
                        )
                    ) : (
                        <KeyHandoverGuestDetailsStep />
                    ))}
                {stepName === "confirmation" && <KeyHandoverBookingSummaryStep />}
                {/* personalData step is not used in the keys flow */}
                {stepName === "payment" && <KeyHandoverPaymentStep />}
            </StepWrapperDiv>

            <ActionsWrapperDiv>
                {step === BookingCreationStep.confirmation ? (
                    <Button
                        size="lg"
                        variant="primary"
                        className="w-full"
                        onClick={() => {
                            dispatch(navigatedToNextStep());
                        }}
                    >
                        {t("book_location.confirmation.order_now")}
                    </Button>
                ) : step === BookingCreationStep.assetGroupSelection ? (
                    <BookLocationNavigateNextAction bounceKey={assetGroupId ?? null} />
                ) : stepName === "orderData" && selectedServiceType === "Key-Pickup-And-Return" ? (
                    <Button
                        size="lg"
                        variant="primary"
                        className={`w-full will-change-transform ${
                            bounceNext ? "motion-safe:animate-pop-once" : ""
                        }`}
                        disabled={!nextEnabled}
                        onClick={() => {
                            const enumRef =
                                useCase === "rental" || useCase === "sup"
                                    ? BookingCreationERStep
                                    : BookingCreationP2PStep;
                            const recipientDataStepNum = getEnumNumberByName(
                                enumRef as any,
                                "recipientData"
                            );
                            dispatch(setKeyRecipientPhase("return"));
                            dispatch(setStep(recipientDataStepNum));
                        }}
                    >
                        {t("book_location.next")}
                    </Button>
                ) : stepName === "recipientData" &&
                  selectedServiceType === "Key-Pickup-And-Return" ? (
                    <Button
                        size="lg"
                        variant="primary"
                        className="w-full"
                        disabled={!nextEnabled}
                        onClick={() => {
                            const enumRef =
                                useCase === "rental" || useCase === "sup"
                                    ? BookingCreationERStep
                                    : BookingCreationP2PStep;
                            if (keyRecipientPhase === "guest") {
                                // After guest data → confirmation
                                const confirmationStepNum = getEnumNumberByName(
                                    enumRef as any,
                                    "confirmation"
                                );
                                dispatch(setStep(confirmationStepNum));
                            } else {
                                // After return scheduling → landlord data
                                const landlordDataStepNum = getEnumNumberByName(
                                    enumRef as any,
                                    "landlordData"
                                );
                                dispatch(setKeyRecipientPhase("guest"));
                                dispatch(setStep(landlordDataStepNum));
                            }
                        }}
                    >
                        {t("book_location.next")}
                    </Button>
                ) : stepName === "landlordData" &&
                  selectedServiceType === "Key-Pickup-And-Return" ? (
                    <Button
                        size="lg"
                        variant="primary"
                        className="w-full"
                        disabled={!nextEnabled}
                        onClick={() => {
                            const enumRef =
                                useCase === "rental" || useCase === "sup"
                                    ? BookingCreationERStep
                                    : BookingCreationP2PStep;
                            // After landlord data → recipient (guest) data
                            const recipientStepNum = getEnumNumberByName(
                                enumRef as any,
                                "recipientData"
                            );
                            dispatch(setKeyRecipientPhase("guest"));
                            dispatch(setStep(recipientStepNum));
                        }}
                    >
                        {t("book_location.next")}
                    </Button>
                ) : stepName === "personalData" &&
                  selectedServiceType === "Key-Pickup-And-Return" ? (
                    <Button
                        size="lg"
                        variant="primary"
                        className="w-full"
                        disabled={!nextEnabled}
                        onClick={() => {
                            const enumRef =
                                useCase === "rental" || useCase === "sup"
                                    ? BookingCreationERStep
                                    : BookingCreationP2PStep;
                            const confirmationStepNum = getEnumNumberByName(
                                enumRef as any,
                                "confirmation"
                            );
                            dispatch(setStep(confirmationStepNum));
                        }}
                    >
                        {t("book_location.next")}
                    </Button>
                ) : step !== BookingCreationStep.payment ? (
                    <BookLocationNavigateNextAction bounceKey={selectedLocation?.id ?? null} />
                ) : null}
            </ActionsWrapperDiv>
            <GeneralDialog
                open={pageDialogOpen}
                onClose={() => setPageDialogOpen(false)}
                onContinue={closePage}
                content={t("use_order.closing_dialog.content")}
            />
        </RootDiv>
    );
};

export default KeyHandoverBookingPage;
